import { useCallback, useEffect, useRef, useState } from "react";
import { Cloud, LogIn, UserPlus, Boxes, Workflow, LayoutDashboard, Smartphone, Download, MonitorDown, Apple } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Capacitor } from "@capacitor/core";
import { TOKEN_KEY } from "../lib/backendClient";
import { setStoredRemoteConfig } from "../lib/remoteConfig";
import { BrandMark } from "./BrandMark";
import { BrandExperience } from "./BrandExperience";

const FEATURE_ICONS = [Boxes, Workflow, LayoutDashboard, Smartphone] as const;
const FEATURE_KEYS = ["Entities", "Workflows", "Dashboards", "Mobile"] as const;
const CLOUD_API_URL = String(import.meta.env.VITE_CLOUD_API_URL || "https://api.blackrednow.com").replace(/\/+$/, "");

interface Props { onConnected: () => void; }
type DownloadPlatform = "windows" | "android";
interface AccountResponse { accountUrl: string; token: string; plan: string; user: { name: string; email: string }; downloadUrl?: string | null; error?: string; }
interface SocialProviders { google: { status: "enabled" | "needs_configuration"; clientId: string | null }; microsoft: { status: "pending" }; apple: { status: "pending" }; }

declare global {
  interface Window {
    google?: { accounts: { id: {
      initialize: (options: { client_id: string; callback: (response: { credential: string }) => void }) => void;
      renderButton: (element: HTMLElement, options: Record<string, unknown>) => void;
    } } };
  }
}

function clientPlatform() {
  if (window.blackredStudio) return "windows";
  if (Capacitor.isNativePlatform()) return Capacitor.getPlatform();
  return "web";
}

/** Acceso cloud único para navegador, Android y Electron. No pide URLs ni claves internas:
 * el gateway resuelve el espacio aislado de la cuenta después de autenticarla. */
export function RemoteConnectGate({ onConnected }: Props) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", acceptTerms: false });
  const [pendingDownload, setPendingDownload] = useState<DownloadPlatform | null>(null);
  const [downloadStarted, setDownloadStarted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [providers, setProviders] = useState<SocialProviders | null>(null);
  const googleButtonRef = useRef<HTMLDivElement>(null);

  const completeAccount = useCallback((payload: Partial<AccountResponse>) => {
    if (!payload.accountUrl || !payload.token) throw new Error(payload.error || t("connect.accountError"));
    setStoredRemoteConfig({ baseUrl: payload.accountUrl, accessKey: "" });
    sessionStorage.setItem(TOKEN_KEY, payload.token);
    onConnected();
  }, [onConnected, t]);

  const submitGoogle = useCallback(async (credential: string) => {
    setBusy(true);
    setError("");
    try {
      const response = await fetch(`${CLOUD_API_URL}/api/accounts/social/google`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ credential, clientPlatform: clientPlatform() }),
      });
      const payload = await response.json().catch(() => ({})) as Partial<AccountResponse>;
      if (!response.ok) throw new Error(payload.error || t("connect.googleError"));
      completeAccount(payload);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : t("connect.googleError"));
    } finally {
      setBusy(false);
    }
  }, [completeAccount, t]);

  useEffect(() => {
    let active = true;
    fetch(`${CLOUD_API_URL}/api/accounts/social/providers`, { headers: { accept: "application/json" } })
      .then(async (response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.json() as Promise<SocialProviders>;
      })
      .then((value) => { if (active) setProviders(value); })
      .catch(() => { if (active) setProviders(null); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const clientId = providers?.google.clientId;
    if (!clientId) return;
    let cancelled = false;
    const render = () => {
      if (cancelled || !window.google || !googleButtonRef.current) return;
      googleButtonRef.current.replaceChildren();
      window.google.accounts.id.initialize({ client_id: clientId, callback: ({ credential }) => void submitGoogle(credential) });
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        type: "standard", theme: "filled_black", size: "large", shape: "rectangular",
        text: mode === "signup" ? "signup_with" : "signin_with", width: Math.min(360, googleButtonRef.current.clientWidth || 360),
      });
    };
    const existing = document.querySelector<HTMLScriptElement>('script[data-blackred-google-identity="true"]');
    if (window.google) render();
    else if (existing) existing.addEventListener("load", render, { once: true });
    else {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.dataset.blackredGoogleIdentity = "true";
      script.addEventListener("load", render, { once: true });
      script.addEventListener("error", () => { if (!cancelled) setError(t("connect.googleLoadError")); }, { once: true });
      document.head.appendChild(script);
    }
    return () => { cancelled = true; existing?.removeEventListener("load", render); };
  }, [providers?.google.clientId, mode, submitGoogle, t]);

  async function submit() {
    const needsProfile = mode === "signup" || Boolean(pendingDownload);
    if (!form.email.trim() || !form.password || (mode === "signup" && !form.name.trim()) || (needsProfile && !form.acceptTerms)) return;
    setBusy(true);
    setError("");
    try {
      const response = await fetch(`${CLOUD_API_URL}/api/accounts/${mode}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(), password: form.password,
          acceptTerms: form.acceptTerms, clientPlatform: clientPlatform(), downloadPlatform: pendingDownload,
        }),
      });
      const payload = await response.json().catch(() => ({})) as Partial<AccountResponse>;
      if (!response.ok || !payload.accountUrl || !payload.token) {
        throw new Error(payload.error || t("connect.accountError", { defaultValue: "No fue posible acceder a la cuenta." }));
      }
      if (pendingDownload && payload.downloadUrl) {
        const anchor = document.createElement("a");
        anchor.href = payload.downloadUrl;
        anchor.rel = "noopener";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        setDownloadStarted(true);
        return;
      } else completeAccount(payload);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : t("connect.accountError", { defaultValue: "No fue posible acceder a la cuenta." }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="view connect-page">
      <div className="connect-brand"><BrandMark size={44} withWordmark /></div>
      <div className="connect-grid">
        <div className="connect-showcase">
          <BrandExperience compact />
          <span className="eyebrow">{t("intro.eyebrow")}</span>
          <h1>{t("intro.title")}</h1>
          <p>{t("connect.cloudBody", { defaultValue: "Tus proyectos se guardan en BlackRed Cloud y están disponibles desde cualquier dispositivo." })}</p>
          <div className="intro-features connect-features">
            {FEATURE_KEYS.map((key, index) => {
              const Icon = FEATURE_ICONS[index];
              return <div className="card intro-feature" key={key}><Icon size={20}/><span><h3>{t(`intro.feature${key}Title`)}</h3><p>{t(`intro.feature${key}Body`)}</p></span></div>;
            })}
          </div>
        </div>

        <div className="connect-panel card">
          <Cloud size={32}/>
          <div>
            <h2>{mode === "login" ? t("connect.accountLogin", { defaultValue: "Inicia sesión" }) : t("connect.accountCreate", { defaultValue: "Crea tu cuenta gratuita" })}</h2>
            <p>{t("connect.accountPrivacy", { defaultValue: "El servidor guarda y sincroniza tu trabajo. Esta modalidad requiere conexión a Internet." })}</p>
          </div>
          <div className="account-mode-tabs">
            <button className={`btn ${mode === "login" ? "primary" : "secondary"}`} onClick={() => { setMode("login"); setError(""); }}><LogIn size={15}/>{t("connect.loginTab", { defaultValue: "Entrar" })}</button>
            <button className={`btn ${mode === "signup" ? "primary" : "secondary"}`} onClick={() => { setMode("signup"); setError(""); }}><UserPlus size={15}/>{t("connect.signupTab", { defaultValue: "Crear cuenta" })}</button>
          </div>
          <div className="social-auth" aria-label={t("connect.socialOptions")}>
            {providers?.google.status === "enabled"
              ? <div className={busy ? "google-signin is-busy" : "google-signin"} ref={googleButtonRef}/>
              : <button className="social-provider" type="button" disabled><span className="google-g">G</span>{t("connect.google")}</button>}
            <div className="social-pending-grid">
              <button className="social-provider" type="button" disabled><span className="microsoft-mark" aria-hidden="true"><i/><i/><i/><i/></span><span>{t("connect.microsoft")}</span><small>{t("connect.pending")}</small></button>
              <button className="social-provider" type="button" disabled><Apple size={18}/><span>{t("connect.apple")}</span><small>{t("connect.pending")}</small></button>
            </div>
            <small className="social-consent">{t("connect.socialConsent")}</small>
          </div>
          <div className="auth-divider"><span>{t("connect.orManual")}</span></div>
          <div className="auth-fields">
            {mode === "signup" && <input aria-label={t("backend.namePlaceholder")} placeholder={t("backend.namePlaceholder")} value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })}/>}
            <input aria-label={t("backend.emailPlaceholder")} type="email" placeholder={t("backend.emailPlaceholder")} value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} autoCapitalize="none" autoCorrect="off"/>
            <input aria-label={t("backend.passwordPlaceholder")} type="password" placeholder={t("backend.passwordPlaceholder")} value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} onKeyDown={(event) => { if (event.key === "Enter") void submit(); }}/>
            {(mode === "signup" || pendingDownload) && <label className="trial-consent"><input type="checkbox" checked={form.acceptTerms} onChange={(event) => setForm({ ...form, acceptTerms: event.target.checked })}/><span>{t("connect.trialConsent")}</span></label>}
            <button className="btn primary" disabled={busy || downloadStarted || !form.email.trim() || form.password.length < 10 || (mode === "signup" && !form.name.trim()) || ((mode === "signup" || Boolean(pendingDownload)) && !form.acceptTerms)} onClick={() => void submit()}>
              {mode === "login" ? <LogIn size={15}/> : <UserPlus size={15}/>}
              {busy ? t("connect.connecting") : mode === "login" ? t("connect.accountLogin", { defaultValue: "Iniciar sesión" }) : t("connect.accountCreate", { defaultValue: "Crear cuenta gratuita" })}
            </button>
          </div>
          {error && <p className="backend-message" role="alert">{error}</p>}
          {downloadStarted && <p className="download-success" role="status">{t("connect.downloadStarted")}</p>}
          <small className="cloud-storage-note">{t("connect.cloudOnly", { defaultValue: "Guardado cloud incluido. El almacenamiento local estará disponible con suscripción." })}</small>
          {!window.blackredStudio && !Capacitor.isNativePlatform() && <div className="download-trial">
            <div><strong><Download size={15}/>{t("connect.downloadTitle")}</strong><small>{t("connect.downloadBody")}</small></div>
            <div className="download-actions">
              <button className={`btn secondary ${pendingDownload === "windows" ? "is-selected" : ""}`} onClick={() => { setPendingDownload("windows"); setMode("signup"); setError(""); }}><MonitorDown size={15}/>{t("connect.downloadWindows")}</button>
              <button className={`btn secondary ${pendingDownload === "android" ? "is-selected" : ""}`} onClick={() => { setPendingDownload("android"); setMode("signup"); setError(""); }}><Smartphone size={15}/>{t("connect.downloadAndroid")}</button>
            </div>
            {pendingDownload && <small className="download-notice">{t("connect.downloadNotice")}</small>}
          </div>}
        </div>
      </div>
    </section>
  );
}
